import React from 'react';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import Alert from '@material-ui/lab/Alert';
import { useGlobalStyles } from 'GlobalStyles';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import {
  DuplicateCandidateOverview,
  DuplicateCandidateSkeleton
} from 'UI/components/molecules/DuplicateCandidateOverview';
import { FORTPAC_SUPPORT_LINK, TALENT_POOL_URL } from 'UI/constants/defaults';
import { CandidateEntity } from 'UI/constants/entityTypes';
import { SeverityBySimilarity } from 'UI/constants/status';

export const DuplicateCandidateAlert = ({ emailValidation, onClickCreateFromContact }) => {
  const globalClasses = useGlobalStyles();

  const { registeredData: registeredEmail, candidateRegisteredInTalentPool } = emailValidation;
  const isRegisteredInTalentPool = Boolean(candidateRegisteredInTalentPool);

  const handleClickCreateFromContact = () => {
    onClickCreateFromContact({
      id: registeredEmail.id,
      company: registeredEmail.company,
      email: registeredEmail.email,
      full_name: registeredEmail.fullName,
      role_id: registeredEmail.entity,
      subtitle: registeredEmail.subtitle
    });
  };

  const alertText = {
    [SeverityBySimilarity.Exact]: {
      alert: {
        text: strings.formatString(strings.candidates.creation.duplicateEmail.alert, {
          email: `"${emailValidation.email}"`,
          entity: `as a ${CandidateEntity.singular}`
        }),
        action: (
          <Button
            classes={{ root: globalClasses.alertActionButton }}
            color="inherit"
            href={FORTPAC_SUPPORT_LINK}
            rel="noopener noreferrer"
            target="_blank"
            variant="outlined"
          >
            {strings.shared.app.support}
          </Button>
        )
      }
    },
    [SeverityBySimilarity.Similar]: {
      alert: {
        text: strings.formatString(strings.candidates.creation.similarEmail.alert, {
          email: `"${emailValidation.email}"`
        }),
        action: (
          <Button
            classes={{ root: globalClasses.alertActionButton }}
            color="inherit"
            disabled={emailValidation.isValidating || false}
            onClick={handleClickCreateFromContact}
            type="button"
            variant="outlined"
          >
            {strings.candidates.creation.similarEmail.alertButton}
          </Button>
        )
      }
    }
  };

  if (isRegisteredInTalentPool) {
    alertText[SeverityBySimilarity.Exact].alert = {
      text: strings.formatString(strings.candidates.creation.registeredInTalentPool.alert, {
        email: `"${emailValidation.email}"`
      }),
      action: (
        <Button
          classes={{ root: globalClasses.alertActionButton }}
          color="inherit"
          href={`${TALENT_POOL_URL}/talent-pool/applicants/${candidateRegisteredInTalentPool.id}`}
          rel="noopener noreferrer"
          target="_blank"
          variant="outlined"
        >
          {strings.candidates.creation.registeredInTalentPool.alertButton}
        </Button>
      )
    };
  }

  return (
    <>
      <Alert
        data-testid="duplicate-alert"
        severity={emailValidation.severity}
        action={alertText[emailValidation.severity].alert.action}
      >
        {alertText[emailValidation.severity].alert.text}
      </Alert>

      <When condition={!isRegisteredInTalentPool}>
        <>
          {emailValidation.isValidating ? (
            <DuplicateCandidateSkeleton />
          ) : (
            <DuplicateCandidateOverview
              key={registeredEmail.id}
              data={registeredEmail}
              handleClickButton={handleClickCreateFromContact}
            />
          )}
        </>
      </When>
      <Divider />
    </>
  );
};
